import {
    Box,
    Card,
    CardContent,
    Typography,
    Avatar,
    Grid,
    Divider,
    TextField,
    MenuItem,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import CakeIcon from "@mui/icons-material/Cake";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import HeightIcon from "@mui/icons-material/Height";
import ScaleIcon from "@mui/icons-material/Scale";
import { formatDate, getGeschlechtLabel } from "./profilUtils";

// Inhalt des „Profil"-Tabs: persönliche Daten, Körperdaten, BMI/Kalorien.
function ProfilTab({ editMode, formData, onInputChange, isMobile }) {
    return (
        <Grid container sx={{ width: "100%", display: "flex", justifyContent: "space-between", flexDirection: isMobile ? "column" : "row", gap: { xs: 2, md: 3 } }}>
            {/* Persönliche Daten */}
            <Grid sx={{ flex: isMobile ? "none" : "1 1 auto" }}>
                <Card sx={{ borderRadius: "16px", height: "100%", background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
                    <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, height: "100%" }}>
                        <Box display="flex" alignItems="center" mb={{ xs: 2.5, md: 3.5 }}>
                            <Avatar sx={{ bgcolor: "linear-gradient(135deg, #3b82f6 0%, #1e3a8a 100%)", mr: { xs: 2, md: 2.5 }, width: { xs: 40, md: 48 }, height: { xs: 40, md: 48 } }}>
                                <PersonIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
                            </Avatar>
                            <Typography variant={{ xs: "h6", md: "h5" }} fontWeight={700} sx={{ color: "#e0f2fe" }}>
                                Persönliche Daten
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: { xs: 2.5, md: 3.5 }, borderColor: "rgba(59, 130, 246, 0.15)" }} />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2.5, md: 3 } }}>
                            <Box>
                                <Box display="flex" alignItems="center" gap={{ xs: 1, md: 1.5 }} mb={{ xs: 1, md: 1.25 }} sx={{ color: "#93c5fd" }}>
                                    <EmailIcon sx={{ fontSize: { xs: 22, md: 24 } }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>E-Mail</Typography>
                                </Box>
                                {editMode ? (
                                    <TextField name="email" type="email" value={formData.email} onChange={onInputChange} fullWidth size="small" />
                                ) : (
                                    <Typography variant="body2" sx={{ color: "#cbd5e1" }}>{formData?.email || "Nicht angegeben"}</Typography>
                                )}
                            </Box>
                            <Box>
                                <Box display="flex" alignItems="center" gap={{ xs: 1, md: 1.5 }} mb={{ xs: 1, md: 1.25 }} sx={{ color: "#93c5fd" }}>
                                    <CakeIcon sx={{ fontSize: { xs: 22, md: 24 } }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Geburtsdatum</Typography>
                                </Box>
                                {editMode ? (
                                    <TextField name="geb_datum" type="date" value={formData.geb_datum} onChange={onInputChange} fullWidth size="small" InputLabelProps={{ shrink: true }} />
                                ) : (
                                    <Typography variant="body2" sx={{ color: "#cbd5e1" }}>{formatDate(formData?.geb_datum)}</Typography>
                                )}
                            </Box>
                            <Box>
                                <Box display="flex" alignItems="center" gap={{ xs: 1, md: 1.5 }} mb={{ xs: 1, md: 1.25 }} sx={{ color: "#93c5fd" }}>
                                    <PersonIcon sx={{ fontSize: { xs: 22, md: 24 } }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Geschlecht</Typography>
                                </Box>
                                {editMode ? (
                                    <TextField name="geschlecht" select value={formData.geschlecht} onChange={onInputChange} fullWidth size="small">
                                        <MenuItem value="m">Männlich</MenuItem>
                                        <MenuItem value="w">Weiblich</MenuItem>
                                        <MenuItem value="d">Divers</MenuItem>
                                    </TextField>
                                ) : (
                                    <Typography variant="body2" sx={{ color: "#cbd5e1" }}>{getGeschlechtLabel(formData?.geschlecht)}</Typography>
                                )}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* Körperdaten */}
            <Grid sx={{ flex: isMobile ? "none" : "1 1 auto" }}>
                <Card sx={{ borderRadius: "16px", height: "100%", background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", border: "1px solid rgba(34, 197, 94, 0.2)" }}>
                    <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, height: "100%" }}>
                        <Box display="flex" alignItems="center" mb={{ xs: 2.5, md: 3.5 }}>
                            <Avatar sx={{ bgcolor: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)", mr: { xs: 2, md: 2.5 }, width: { xs: 40, md: 48 }, height: { xs: 40, md: 48 } }}>
                                <FitnessCenterIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
                            </Avatar>
                            <Typography variant={{ xs: "h6", md: "h5" }} fontWeight={700} sx={{ color: "#e0f2fe" }}>Körperdaten</Typography>
                        </Box>
                        <Divider sx={{ mb: { xs: 2.5, md: 3.5 }, borderColor: "rgba(34, 197, 94, 0.15)" }} />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2.5, md: 3 } }}>
                            <Box>
                                <Box display="flex" alignItems="center" gap={{ xs: 1, md: 1.5 }} mb={{ xs: 1, md: 1.25 }} sx={{ color: "#6ee7b7" }}>
                                    <HeightIcon sx={{ fontSize: { xs: 22, md: 24 } }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Größe (cm)</Typography>
                                </Box>
                                {editMode ? (
                                    <TextField name="groesse" type="number" value={formData.groesse} onChange={onInputChange} fullWidth size="small" inputProps={{ min: 0, step: 1 }} />
                                ) : (
                                    <Typography variant="body2" sx={{ color: "#cbd5e1" }}>{formData?.groesse ? `${formData.groesse} cm` : "Nicht angegeben"}</Typography>
                                )}
                            </Box>
                            <Box>
                                <Box display="flex" alignItems="center" gap={{ xs: 1, md: 1.5 }} mb={{ xs: 1, md: 1.25 }} sx={{ color: "#6ee7b7" }}>
                                    <ScaleIcon sx={{ fontSize: { xs: 22, md: 24 } }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Aktuelles Gewicht (kg)</Typography>
                                </Box>
                                {editMode ? (
                                    <TextField name="gewicht" type="number" value={formData.gewicht} onChange={onInputChange} fullWidth size="small" inputProps={{ min: 0, step: 0.1 }} />
                                ) : (
                                    <Typography variant="body2" sx={{ color: "#cbd5e1" }}>{formData?.gewicht ? `${formData.gewicht} kg` : "Nicht angegeben"}</Typography>
                                )}
                            </Box>
                            <Box>
                                <Box display="flex" alignItems="center" gap={{ xs: 1, md: 1.5 }} mb={{ xs: 1, md: 1.25 }} sx={{ color: "#6ee7b7" }}>
                                    <ScaleIcon sx={{ fontSize: { xs: 22, md: 24 } }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Startgewicht (kg)</Typography>
                                </Box>
                                {editMode ? (
                                    <TextField name="start_gewicht" type="number" value={formData.start_gewicht} onChange={onInputChange} fullWidth size="small" inputProps={{ min: 0, step: 0.1 }} />
                                ) : (
                                    <Typography variant="body2" sx={{ color: "#cbd5e1" }}>{formData?.start_gewicht ? `${formData.start_gewicht} kg` : "Nicht angegeben"}</Typography>
                                )}
                            </Box>
                            <Box>
                                <Box display="flex" alignItems="center" gap={{ xs: 1, md: 1.5 }} mb={{ xs: 1, md: 1.25 }} sx={{ color: "#6ee7b7" }}>
                                    <ScaleIcon sx={{ fontSize: { xs: 22, md: 24 } }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Zielgewicht (kg)</Typography>
                                </Box>
                                {editMode ? (
                                    <TextField name="ziel_gewicht" type="number" value={formData.ziel_gewicht} onChange={onInputChange} fullWidth size="small" inputProps={{ min: 0, step: 0.1 }} />
                                ) : (
                                    <Typography variant="body2" sx={{ color: "#cbd5e1" }}>{formData?.ziel_gewicht ? `${formData.ziel_gewicht} kg` : "Nicht angegeben"}</Typography>
                                )}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* BMI und Kalorien */}
            {formData?.gewicht && formData?.groesse && (
                <Grid sx={{ flex: isMobile ? "none" : "1 1 auto", display: "flex", flexDirection: "column", gap: { xs: 2, md: 3 } }}>
                    <Card sx={{ borderRadius: "16px", height: "100%", background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", border: "1px solid rgba(234, 179, 8, 0.2)" }}>
                        <CardContent sx={{ p: { xs: 2.5, md: 3 }, display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
                            <Box>
                                <Typography variant={{ xs: "subtitle2", md: "subtitle1" }} fontWeight={700} sx={{ color: "#e0f2fe" }}>Body Mass Index</Typography>
                                <Divider sx={{ mt: 1, mb: 1.5, borderColor: "rgba(234, 179, 8, 0.15)" }} />
                            </Box>
                            <Box display="flex" alignItems="center" gap={1.5} flexGrow={1}>
                                <Box sx={{ background: "linear-gradient(135deg, rgba(234, 179, 8, 0.15) 0%, rgba(59, 130, 246, 0.1) 100%)", borderRadius: '16px', padding: "12px 16px", border: "1px solid rgba(234, 179, 8, 0.3)", textAlign: "center" }}>
                                    <Typography variant="h4" sx={{ color: "#eab308", fontWeight: 700, fontSize: "1.875rem" }}>
                                        {(formData.gewicht / ((formData.groesse / 100) ** 2)).toFixed(1)}
                                    </Typography>
                                </Box>
                                <Box flexGrow={1}>
                                    <Typography variant="caption" sx={{ color: "#93c5fd", fontSize: "0.75rem", display: "block" }}>BMI Wert</Typography>
                                    <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem" }}>{formData.gewicht} kg / {formData.groesse} cm</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ borderRadius: "16px", height: "100%", background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", border: "1px solid rgba(239, 68, 68, 0.2)" }}>
                        <CardContent sx={{ p: { xs: 2.5, md: 3 }, display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
                            <Box>
                                <Typography variant={{ xs: "subtitle2", md: "subtitle1" }} fontWeight={700} sx={{ color: "#e0f2fe" }}>Kalorienverbrauch</Typography>
                                <Divider sx={{ mt: 1, mb: 1.5, borderColor: "rgba(239, 68, 68, 0.15)" }} />
                            </Box>
                            <Box flexGrow={1}>
                                <Box mb={2}>
                                    <Typography variant="caption" sx={{ color: "#93c5fd", fontSize: "0.75rem", display: "block", mb: 0.5 }}>Täglicher Verbrauch (BMR)</Typography>
                                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                                        <Typography variant="h5" sx={{ color: "#ef4444", fontWeight: 700, fontSize: "1.5rem" }}>
                                            {formData.gewicht && formData.groesse && formData.geb_datum
                                                ? Math.round(formData.geschlecht === "m"
                                                    ? 88.362 + 13.397 * formData.gewicht + 4.799 * formData.groesse - 5.677 * (new Date().getFullYear() - new Date(formData.geb_datum).getFullYear())
                                                    : 447.593 + 9.247 * formData.gewicht + 3.098 * formData.groesse - 4.33 * (new Date().getFullYear() - new Date(formData.geb_datum).getFullYear()))
                                                : "—"}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "#cbd5e1", fontSize: "0.75rem" }}>kcal</Typography>
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: "#93c5fd", fontSize: "0.75rem", display: "block", mb: 0.5 }}>Mit moderater Aktivität</Typography>
                                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                                        <Typography variant="h5" sx={{ color: "#fbbf24", fontWeight: 700, fontSize: "1.5rem" }}>
                                            {formData.gewicht && formData.groesse && formData.geb_datum
                                                ? Math.round((formData.geschlecht === "m"
                                                    ? 88.362 + 13.397 * formData.gewicht + 4.799 * formData.groesse - 5.677 * (new Date().getFullYear() - new Date(formData.geb_datum).getFullYear())
                                                    : 447.593 + 9.247 * formData.gewicht + 3.098 * formData.groesse - 4.33 * (new Date().getFullYear() - new Date(formData.geb_datum).getFullYear())) * 1.55)
                                                : "—"}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "#cbd5e1", fontSize: "0.75rem" }}>kcal</Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.65rem", mt: 0.5, display: "block" }}>BMR × 1.55 (Sport 3-4x/Woche)</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            )}
        </Grid>
    );
}

export default ProfilTab;
